import { z } from 'zod';
import type { ReactNode } from 'react';

// Tipos compartilhados entre os 4 dominios kanban (fundiario, ambiental, credito, juridico).
// Os endpoints variam ligeiramente por dominio — DOMINIO_CONFIG em api.ts resolve isso.

export const kanbanCardSchema = z
  .object({
    id: z.union([z.coerce.number(), z.string()]).transform(String),
    cliente_nome: z.string().nullish(),
    cliente_cpf: z.string().nullish(),
    imovel_matricula: z.string().nullish(),
    imovel_denominacao: z.string().nullish(),
    municipio: z.string().nullish(),
    kanban_coluna: z.union([z.coerce.number(), z.string()]).nullish(),
    dias_na_coluna: z.coerce.number().nullish(),
    // Pode vir 'ok' | 'atencao' | 'critico' | 'concluido' | outros futuros.
    // Mantemos string permissiva — KanbanCard só renderiza chip pros 3 conhecidos.
    sla_status: z.string().nullish(),
    // Back-end retorna array de {id, name, email} (não strings).
    // Aceitamos ambos via union pra tolerância.
    responsaveis: z
      .array(
        z.union([
          z.string(),
          z
            .object({
              id: z.coerce.number().or(z.string()),
              name: z.string().nullish(),
              email: z.string().nullish(),
            })
            .passthrough(),
        ]),
      )
      .default([]),
    tipo_processo: z.string().nullish(),
    pipe: z.string().nullish(),
    status: z.string().nullish(),
    // Campos extra por dominio ficam em "extra" (passamos forward sem schema)
  })
  .passthrough();
export type KanbanCard = z.infer<typeof kanbanCardSchema> & Record<string, unknown>;

export const kanbanColumnSchema = z.object({
  id: z.union([z.coerce.number(), z.string()]).transform(String),
  nome: z.string(),
  cor: z.string().nullish(),
  sla_dias: z.coerce.number().nullish(),
  processos: z.array(kanbanCardSchema).default([]),
});
export type KanbanColumn = z.infer<typeof kanbanColumnSchema>;

export const kanbanPipeSchema = z.object({
  id: z.string(),
  nome: z.string(),
  ativo: z.boolean().default(true),
  count: z.coerce.number().nullish(),
});
export type KanbanPipe = z.infer<typeof kanbanPipeSchema>;

export const kanbanResponseSchema = z.object({
  pipes: z.array(kanbanPipeSchema).optional(),
  pipe_atual: z.string().nullish(),
  colunas: z.array(kanbanColumnSchema).default([]),
});
export type KanbanResponse = z.infer<typeof kanbanResponseSchema>;

// FieldMap: define quais campos do card aparecem em cada slot da UI.
// Permite cada dominio customizar o card sem reescrever o componente.
export interface CardSlot {
  label: string;
  value: string | null;
  tone?: 'neutral' | 'accent' | 'ok' | 'warn' | 'danger' | 'info';
}

export type CardFieldMap = (card: KanbanCard) => {
  title: string;
  subtitle?: string | null;
  chips?: CardSlot[];
  meta?: string | null;
  /** Badge no topo-direito do card (ex.: logo da instituição no domínio crédito). */
  logo?: ReactNode;
};

export type DetailFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'date'
  | 'boolean'
  | 'select'
  | 'multiselect'
  | 'responsaveis'
  | 'triagem_aceite'
  | 'emitir_orcamento'
  | 'emitir_formulario_prioridade'
  | 'emitir_formulario_pra'
  | 'emitir_formulario_alteracao_email_sicar'
  | 'emitir_formulario_cancelamento_car'
  | 'emitir_proposta_fundiaria'
  | 'emitir_prada_simplificado';

export type ChipTone =
  | 'neutral'
  | 'accent'
  | 'ok'
  | 'warn'
  | 'danger'
  | 'info';

export interface UserRef {
  id: number;
  name?: string | null;
  email?: string | null;
}

export type DetailFieldMap = (card: KanbanCard) => Array<{
  label: string;
  helpText?: string;
  value: React.ReactNode;
  section?: string;
  // Identifica em qual aba o campo é renderizado quando o DetailDrawer
  // está em modo 'tabs'. Sem efeito quando o drawer não tem tabs.
  tab?: string;
  alwaysShow?: boolean;
  // Quando definido, o campo vira editável e auto-salva no blur via PATCH.
  field?: string;
  type?: DetailFieldType;
  // Por padrão o PATCH vai pro processo. 'imovel' direciona pro registro
  // do imóvel vinculado (PATCH /api/v2/imoveis/:imovel_id).
  entity?: 'processo' | 'imovel';
  // Valor cru (não formatado) — usado no input. Se omitido, parte de String(value).
  rawValue?: string | number | boolean | string[] | null;
  // Para type='select'
  options?: Array<{ value: string; label: string }>;
  // Para type='responsaveis'
  responsaveis?: UserRef[];
  // Para type='triagem_aceite'
  triagemAssistenteNome?: string | null;
  triagemAssistenteStatus?: 'pendente' | 'aceito' | null;
  // Renderiza o display como Chip com este tom — útil pra status.
  chipTone?: ChipTone;
}>;

export type Dominio = 'fundiario' | 'ambiental' | 'credito' | 'juridico';
